import { useMemo } from "react";
import { citationByDocumentId } from "../../lib/trailCitations";
import { type AnalyticsColumnResult } from "../../models/audit/analyticsColumnResult";
import {
  type AuditSession,
  type UploadedDocument,
} from "../../models/audit/auditSession";
import { type Citation } from "../../models/audit/citation";

/**
 * The step cards behind one analytics verdict, resolved and ready to place.
 *
 * **This is the one resolution, and both panes make it.** The review pane's
 * `KEY WORKFLOW STEPS` renders the starred subset and the Deterministic Steps
 * pane renders the run's steps; they are siblings behind one toggle rather than
 * one containing the other, so the resolution lives here instead of in whichever
 * of them was written first. Every card the run produced is returned, in the
 * order the run logged its steps, each carrying its own star — a caller filters,
 * it does not re-resolve.
 *
 * **A card is found through the manifest and through nothing else.** The card
 * document carries the run id and the step id in its payload, and that pair is
 * the whole of the step-to-card link: no column joins a step to its card, and
 * nothing derives a card's id in order to read one back. So the run's own step
 * log names the steps, and a card answers for a step only when it agrees with
 * both halves of that pair.
 *
 * **The cards are looked for in the session's own document table** — the
 * documents the session arrives with, the same table attribute tests resolve
 * their agent screenshots from. The session's documents route is not that
 * table: it hides what the agent registers, as the Documents tab does, and a
 * card is registered by the agent.
 *
 * The evidence lists are deliberately not the path either. A card is excluded
 * from all four of them by design — it is registered by the agent, it has no
 * match item, it carries no category, and it parents to nothing — and each of
 * those exclusions is load-bearing somewhere else.
 *
 * **A card that does not resolve is omitted and the verdict still renders.** A
 * card is evidence about a step, not a precondition of judging one, so a missing
 * document or a log this build cannot read costs that card and never the pane.
 */

/** The child type the card renderer registers a card's document under. */
const CARD_CHILD_TYPE = "step_card";

/**
 * The card payload's two keys.
 *
 * Raw snake_case: they are entries in a free-form payload column rather than
 * fields of a model, so the wire's camel-casing never reaches them.
 */
const CARD_RUN_ID_KEY = "analytics_run_id";
const CARD_STEP_ID_KEY = "analytics_step_id";

/** One step's card: what it is of, and the document to open. */
export interface AnalyticsStepCard {
  /** The run's own id for the step, as its log and its card both name it. */
  stepId: string;
  stepName: string;
  /** The card document, for a caller that opens it in the viewer. */
  documentId: string;
  fileName: string;
  /**
   * Starred: this card is included in the workpaper. A read of the citation's
   * own include flag, kept beside the citation so a caller filters the starred
   * subset without reaching into it.
   */
  isKey: boolean;
  /**
   * The citation anchored to this card's document, or null where the trail
   * cites none. The star is this citation's own flag, so a caller writes the
   * include state straight to it and never derives it back through the
   * run-steps read a step row is composed from.
   */
  citation: Citation | null;
}

/**
 * Which steps the server rendered a card for and which document each card is,
 * where this build can establish it.
 *
 * Reported as its own state rather than inferred from `resolved`. An empty
 * `resolved` list is a run that wrote no card, a verdict naming no run, and a
 * verdict whose steps cannot be named yet, and a caller asking what the server
 * did must not be answered with the absence those three share. This union keeps
 * them apart.
 *
 * The document id rides along so a step's citation can be anchored to the card
 * document, which is settled here.
 */
export type StepCardDocuments =
  /**
   * The verdict answered, for a run whose step log this build can read, so this
   * map is the whole of what the server wrote: a step absent from it is a step
   * the run rendered no card for, which is the one thing an empty entry may
   * mean.
   *
   * Reached only on both, never by falling past the other arms. Each of them is
   * also how a verdict still in flight looks, and this is the strongest claim in
   * the union — the one arm that licenses a caller to withhold a row.
   */
  | {
      kind: "read";
      /** The card document each of those steps has, by the step's own id. */
      cardDocumentIdByStepId: ReadonlyMap<string, string>;
    }
  /**
   * The verdict names no run, so there are no step cards to have read and no
   * run to make a claim about — an auditor's own verdict reaches this state,
   * and nothing about it failed.
   *
   * Separate from `read` with an empty map, which says a run wrote no cards,
   * and separate from `unreadable`, which says the steps could not be named. A
   * pane showing the starred cards has nothing to show and nothing to report; a
   * pane accounting for a run's steps has no run whose cards it could establish.
   */
  | { kind: "noRun" }
  /**
   * The verdict is not in hand, or names a run whose step log this build cannot
   * read, so no step's card can be called present or absent and an empty set
   * states nothing about the run.
   */
  | { kind: "unreadable" };

export interface UseAnalyticsStepCardsResult {
  /** Every card that resolved, in the order the run logged its steps. */
  resolved: AnalyticsStepCard[];
  /** Which steps have a card document at all. */
  cardDocuments: StepCardDocuments;
}

/** The payload's value for one key, when it is a string and not something else. */
function payloadString(
  extra: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = extra[key];
  return typeof value === "string" ? value : undefined;
}

/**
 * The cards this run wrote, keyed by the step each answers for.
 *
 * Where a step somehow has two cards, the one the trail cites wins: a star is
 * the citation's own flag, so an uncited card is one no auditor can mark as
 * reaching the workpaper, and picking the cited one keeps the rendered card and
 * the card a star can reach the same card.
 *
 * **Between two cards the trail treats alike — both cited or neither — the
 * later `createdAt` wins, and on equal timestamps the greater id.** Neither is
 * better evidence than the other, but the session's document table arrives in
 * no guaranteed order, so a rule keyed on read order would let the rendered
 * card change between two reads of the same documents. This is not the
 * first-wins rule the citation resolver states and does not contradict it: that
 * rule settles which citation answers for one document, and these are two
 * documents.
 *
 * `citedDocumentIds` is asked only whether a document is cited, so the citation
 * map itself is passed and no set is built to ask it.
 */
function cardDocumentsByStepId(
  sessionDocuments: AuditSession["documents"],
  runId: string,
  citedDocumentIds: { has(documentId: string): boolean },
): Map<string, { documentId: string; fileName: string }> {
  const byStepId = new Map<string, UploadedDocument>();

  for (const sessionDocument of sessionDocuments) {
    const document = sessionDocument.fetchableDocument;
    if (document === null || document.childType !== CARD_CHILD_TYPE) continue;
    if (payloadString(document.extra, CARD_RUN_ID_KEY) !== runId) continue;

    const stepId = payloadString(document.extra, CARD_STEP_ID_KEY);
    if (stepId === undefined) continue;

    const held = byStepId.get(stepId);
    if (held === undefined || cardOutranks(document, held, citedDocumentIds)) {
      byStepId.set(stepId, document);
    }
  }

  return new Map(
    Array.from(byStepId, ([stepId, document]) => [
      stepId,
      { documentId: document.id, fileName: document.fileName },
    ]),
  );
}

/** Whether `candidate` should replace `held` as one step's card. */
function cardOutranks(
  candidate: UploadedDocument,
  held: UploadedDocument,
  citedDocumentIds: { has(documentId: string): boolean },
): boolean {
  const candidateCited = citedDocumentIds.has(candidate.id);
  if (candidateCited !== citedDocumentIds.has(held.id)) {
    return candidateCited;
  }
  const candidateCreated = createdAtMillis(candidate);
  const heldCreated = createdAtMillis(held);
  if (candidateCreated !== heldCreated) {
    return candidateCreated > heldCreated;
  }
  return candidate.id > held.id;
}

/**
 * The document's creation time, with an unparseable timestamp ranked earliest
 * so the comparison stays a total order and still falls through to the id.
 */
function createdAtMillis(document: UploadedDocument): number {
  const millis = Date.parse(document.createdAt);
  return Number.isNaN(millis) ? Number.NEGATIVE_INFINITY : millis;
}

/**
 * Whether the verdict names a run whose step log this build cannot read.
 *
 * The step log names the steps a card is matched against, and a log this build
 * cannot read leaves every card unmatchable — a manifest is parsed leniently, so
 * one whose shape has moved past this build arrives as the absent one, and
 * `runManifestId` is what separates that from a verdict naming no run at all.
 *
 * It lands as the empty set a run that rendered no cards also lands as, which is
 * why the condition is reported rather than left to be inferred from it.
 */
function stepLogUnreadable(result: AnalyticsColumnResult): boolean {
  const manifest = result.runManifest;
  return manifest === null
    ? result.runManifestId !== null
    : manifest.stepLog === null;
}

/**
 * Whether the verdict names no run at all.
 *
 * Both halves are needed and they say different things: no manifest is also
 * how a manifest this build cannot parse arrives, and `runManifestId` is what
 * says whether there was a run behind it. Only with both null is there nothing
 * to have read — the state an auditor's own verdict reaches, where no step card
 * exists and no read of one failed.
 */
function namesNoRun(result: AnalyticsColumnResult): boolean {
  return result.runManifest === null && result.runManifestId === null;
}

export function useAnalyticsStepCards(
  sessionDocuments: AuditSession["documents"],
  result: AnalyticsColumnResult | undefined,
): UseAnalyticsStepCardsResult {
  const resolved = useMemo<AnalyticsStepCard[]>(() => {
    const manifest = result?.runManifest;
    // A null log is a log this build could not read rather than a run that
    // logged nothing, and a run whose steps cannot be named cannot have its
    // cards matched against them.
    if (!manifest || manifest.stepLog === null) return [];

    const citations = citationByDocumentId(result?.auditorTrail);

    const cardsByStepId = cardDocumentsByStepId(
      sessionDocuments,
      manifest.automationWorkflowRunId,
      citations,
    );

    return manifest.stepLog.flatMap((entry) => {
      const card = cardsByStepId.get(entry.stepId);
      if (card === undefined) return [];
      const citation = citations.get(card.documentId) ?? null;
      return [
        {
          stepId: entry.stepId,
          stepName: entry.stepName,
          documentId: card.documentId,
          fileName: card.fileName,
          isKey: citation?.isKey === true,
          citation,
        },
      ];
    });
  }, [sessionDocuments, result]);

  const cardDocuments = useMemo<StepCardDocuments>(() => {
    // A verdict still in flight names no steps yet, and a caller reads an empty
    // `read` map as the server having written no cards — which would withhold
    // every publish row for the length of a fetch.
    if (result === undefined) return { kind: "unreadable" };
    if (namesNoRun(result)) return { kind: "noRun" };
    if (stepLogUnreadable(result)) return { kind: "unreadable" };
    return {
      kind: "read",
      cardDocumentIdByStepId: new Map(
        resolved.map((card) => [card.stepId, card.documentId]),
      ),
    };
  }, [result, resolved]);

  return { resolved, cardDocuments };
}
